import { format } from 'date-fns';
import { getProbabilityColorFromScheme } from '@/constants/timeline';
import type { ColorScheme } from '@/types/timeline';

export interface DueDateMarkerProps {
  /** X position in pixels */
  x: number;
  /** Y position of the bar (marker extends above and below) */
  barY: number;
  /** Height of the bar in pixels */
  barHeight: number;
  /** Due date to display */
  dueDate: Date;
  /** Probability of meeting the due date (0-1) */
  probability: number | null;
  /** Active color scheme for theme-aware probability colors */
  activeColors: ColorScheme;
  /** Minimum X position to prevent label clipping (default: 0) */
  minX?: number;
}

/** Badge dimensions - sized for full ISO date + probability */
const BADGE_WIDTH = 110;
const BADGE_HEIGHT = 16;
const BADGE_HALF_WIDTH = BADGE_WIDTH / 2;

/** Line styling */
const LINE_WIDTH = 2;
const LINE_OUTLINE_WIDTH = 4;

/**
 * Due date marker atom - renders a vertical line with probability badge
 *
 * Shows due date position on timeline with color-coded probability indicator.
 * Color follows probability thresholds using the active color scheme:
 * red < 50% < orange < 70% < yellow < 85% < green < 95% < dark green.
 *
 * Due dates that fall before the timeline start are not rendered here —
 * they are shown as warning icons in the TimelineLabelColumn instead.
 *
 * Special cases:
 * - Near left edge: Adjusts badge position to prevent clipping
 */
export function DueDateMarker({
  x,
  barY,
  barHeight,
  dueDate,
  probability,
  activeColors,
  minX = 0,
}: DueDateMarkerProps) {
  // Color follows probability thresholds using active scheme
  const probabilityColor = getProbabilityColorFromScheme(probability, activeColors);

  // Calculate badge position - adjust if too close to left edge
  const badgeLeftEdge = x - BADGE_HALF_WIDTH;
  const needsRightShift = badgeLeftEdge < minX;
  const badgeX = needsRightShift ? minX : badgeLeftEdge;
  const textX = needsRightShift ? minX + BADGE_HALF_WIDTH : x;

  // Format: "2025-03-15 · 85%" (full ISO date)
  const dateStr = format(dueDate, 'yyyy-MM-dd');
  const probStr = `${probability !== null ? Math.round(probability * 100) : 0}%`;
  const labelText = `${dateStr} · ${probStr}`;

  return (
    <g>
      {/* Due date badge with date and probability */}
      <rect x={badgeX} y={barY - 20} width={BADGE_WIDTH} height={BADGE_HEIGHT} fill={probabilityColor} rx={3} />
      <text x={textX} y={barY - 9} fill="white" fontSize={9} textAnchor="middle" fontWeight={600}>
        {labelText}
      </text>

      {/* Vertical due date line with contrasting outline for visibility */}
      {/* Dark/light outline to stand out against any background */}
      <line
        x1={x}
        y1={barY - 4}
        x2={x}
        y2={barY + barHeight + 4}
        stroke="white"
        strokeWidth={LINE_OUTLINE_WIDTH}
        opacity={0.8}
      />
      <line x1={x} y1={barY - 4} x2={x} y2={barY + barHeight + 4} stroke={probabilityColor} strokeWidth={LINE_WIDTH} />
    </g>
  );
}
